// הצמדה לסוף לשיחות (בינה-1/2 ו"שאל" של בינה-3): כשהמשתמש בסוף (≤ 80px), התוכן שגדל — טקסט זורם, כרטיסי כלי,
// טבלאות/גרפים שמצוירים מאוחר, תמונות ו-iframe שמשנים גובה — נשאר גלוי. ResizeObserver על התוכן (לא תלות במספר
// ההודעות). גלילה למעלה מנתקת את ההצמדה עד שחוזרים לסוף או לוחצים "↓ לסוף"; שליחה תמיד מצמידה מחדש.
// מכל הגלילה = ה-ancestor הקרוב שגולל בפועל, אחרת המסמך (בבינה-1/2 גולל המסמך — אין מכל פנימי).
// "הסוף" = תחתית סמן (end) שאחרי שדה הכתיבה, מול תחתית המסך פחות scroll-padding (הסרגל התחתון במובייל).
import { useCallback, useEffect, useRef, useState } from 'react';
import { T } from '../../lib/strings.ts';
import { cn } from '../../lib/utils.ts';

const THRESHOLD = 80;

function scrollerOf(el: HTMLElement): HTMLElement {
  for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if ((oy === 'auto' || oy === 'scroll' || oy === 'overlay') && p.scrollHeight > p.clientHeight) return p;
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

/** כמה פיקסלים הסמן מתחת לתחתית הגלויה (שלילי = גלוי), והאם המכל כבר בסוף הגלילה. */
function measure(sc: HTMLElement, end: HTMLElement) {
  const isDoc = sc === document.scrollingElement || sc === document.documentElement;
  const pad = parseFloat(getComputedStyle(isDoc ? document.documentElement : sc).scrollPaddingBottom) || 0;
  const bottom = (isDoc ? window.innerHeight : sc.getBoundingClientRect().bottom) - pad;
  const gap = end.getBoundingClientRect().bottom - bottom;
  const atMax = sc.scrollHeight - sc.scrollTop - sc.clientHeight <= 2;
  return { gap, atEnd: gap <= THRESHOLD || atMax };
}

/** contentRef — על התוכן שגדל; endRef — על סמן ריק אחרי שדה הכתיבה. callback refs: התוכן יכול להופיע אחרי טעינה. */
export function useStickToBottom({ initial = true } = {}) {
  const [c, contentRef] = useState<HTMLElement | null>(null);
  const [e, endRef] = useState<HTMLElement | null>(null);
  const pinned = useRef(initial);
  const lastTop = useRef(0);
  const [atEnd, setAtEnd] = useState(true);

  const follow = useCallback(() => {
    if (!c || !e) return;
    const sc = scrollerOf(c);
    const { gap } = measure(sc, e);
    if (gap > 0) sc.scrollTo({ top: sc.scrollTop + gap, behavior: 'instant' });
    lastTop.current = sc.scrollTop;
    setAtEnd(true);
  }, [c, e]);

  /** שחזור מקום שמור (web-screen-budget): מנתק את ההצמדה — התוכן שנטען לא גולל לסוף; חזרה לסוף/שליחה מצמידות מחדש. */
  const release = useCallback(() => {
    pinned.current = false;
  }, []);

  /** "↓ לסוף" ושליחה: מצמיד מחדש וגולל לסוף. */
  const toEnd = useCallback(() => {
    pinned.current = true;
    follow();
  }, [follow]);

  useEffect(() => {
    if (!c || !e) return;
    lastTop.current = scrollerOf(c).scrollTop;
    // גלילה (של המסמך או של מכל פנימי — capture תופס את שניהם): בסוף → מוצמד; עלייה למעלה → מנותק.
    // גלילה תכנותית לסוף רק מעלה את scrollTop, וגידול תוכן לא משנה אותו — לכן רק המשתמש מנתק.
    const onScroll = () => {
      const sc = scrollerOf(c);
      const m = measure(sc, e);
      if (m.atEnd) pinned.current = true;
      else if (sc.scrollTop < lastTop.current - 1) pinned.current = false;
      lastTop.current = sc.scrollTop;
      setAtEnd(m.atEnd);
    };
    const onGrow = () => {
      if (pinned.current) follow();
      else setAtEnd(measure(scrollerOf(c), e).atEnd);
    };
    const ro = new ResizeObserver(onGrow);
    ro.observe(c);
    // גם מיד אחרי שינוי ב-DOM (microtask, באותה משימה שהוסיפה את הטקסט) — ResizeObserver רץ רק בפריים הבא; כשיש אנימציה
    // רציפה בדף (שורת העבודה של התור החי, 11) הפריימים מסונכרנים ל-vsync, ובחלון הזה קוראים מבחוץ ראו את התוכן לפני ההצמדה
    const mo = new MutationObserver(onGrow);
    mo.observe(c, { childList: true, subtree: true, characterData: true });
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onGrow);
    return () => {
      ro.disconnect();
      mo.disconnect();
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onGrow);
    };
  }, [c, e, follow]);

  return { contentRef, endRef, atEnd, toEnd, release };
}

/** "↓ לסוף" — מוצג כשהמשתמש גלל למעלה בזמן שהתשובה עוד נכתבת. */
export function JumpToEnd({ show, onClick, className }: { show: boolean; onClick: () => void; className?: string }) {
  if (!show) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={T.ai.toEndLabel}
      data-testid="jump-to-end"
      className={cn(
        'inset-x-0 mx-auto flex min-h-10 w-fit cursor-pointer items-center gap-1.5 rounded-sm border border-border bg-card px-4 text-sm font-semibold text-foreground shadow-card transition-colors hover:bg-muted',
        className,
      )}
    >
      <span aria-hidden="true">↓</span>
      {T.ai.toEnd}
    </button>
  );
}
